import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import SpeakerNotesOffOutlined from "@mui/icons-material/SpeakerNotesOffOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { brown, grey, orange, red } from "@mui/material/colors";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getDaysAgo } from "../../../_src_shared/utils/functions/getDaysAgo";
import { htmlStringToText } from "../../../_src_shared/utils/functions/htmlStringToText";
import { formatDateShort } from "../../../utils/dates/formatDateShort";
import { getUserFullNameFromEmail } from "../../../utils/functions/getUserFullNameFromEmail";
import LoanCommentsSideCardPreview from "../../common/Loan/LoanCommentsSideCardPreview";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import ChipItem from "../../ui/ChipItem";

const CellRendererLoanComments = (params) => {
  const [show, setShow] = useState(false);
  const users = useSelector((state) => state.users);
  const loanComments = useMemo(
    () => params?.data?.comments || [],
    [params?.data?.comments]
  );

  const styles = useMemo(() => {
    return {
      root: { pl: 5 },
      commentText: {
        overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: 4,
        WebkitBoxOrient: "vertical",
        maxHeight: "200px",
        lineHeight: "1.6em",
      },
    };
  }, []);

  const commentItem = useMemo(() => {
    if (isEmpty(params?.data?.comments)) {
      return {};
    }
    return params?.data?.comments[0] || {};
  }, [params?.data?.comments]);

  const commentUserEmail = useMemo(() => {
    if (commentItem?.submittedBy) {
      return commentItem?.submittedBy;
    }
    if (commentItem?.postedBy) {
      return commentItem?.postedBy;
    }
  }, [commentItem]);

  const updatedDateValue = useMemo(() => {
    const val = commentItem?.commentDateTime;
    if (isNil(val)) {
      return "N/A";
    }
    const daysAgo = getDaysAgo(val);

    if (daysAgo === 0) {
      return "Today";
    }
    if (daysAgo === 1) {
      return "1 day ago";
    }

    return `${daysAgo} days ago`;
  }, [commentItem?.commentDateTime]);

  return (
    <>
      {isEmpty(params?.data?.comments) ? (
        <Box
          role="button"
          onClick={() => setShow(true)}
          sx={{
            opacity: 0.55,
            display: "flex",
            alignItems: "center",
            gap: 1.8,
            px: 1,
          }}
        >
          <LoanCommentsBadge loanComments={loanComments} />
          <Txt secondary>No comments added. Click here to add a comment</Txt>
        </Box>
      ) : (
        <Box
          sx={{ display: "flex", flexDirection: "column", px: 1 }}
          role="button"
          onClick={() => setShow(true)}
        >
          <Flx
            ac
            gap={1}
            sx={{
              height: "28px",
              // height: "24px",
              //
            }}
          >
            <LoanCommentsBadge loanComments={loanComments} setShow={setShow} />
            <Txt
              secondary
              sx={{
                pl: 0.7,
                fontWeight: 600,
              }}
            >
              {getUserFullNameFromEmail(users, commentUserEmail)} (
              {formatDateShort(commentItem?.commentDateTime)})
            </Txt>
            <ChipItem
              value={updatedDateValue}
              color={
                getDaysAgo(params?.data?.updateDateTime) > 31
                  ? brown["A700"]
                  : null
              }
              // backgroundColor={
              //   getDaysAgo(params?.data?.updateDateTime) > 31
              //     ? orange[50]
              //     : grey[100]
              // }
            />
          </Flx>
          <Txt secondary sx={styles.commentText}>
            {htmlStringToText(commentItem?.htmlString)}
          </Txt>
        </Box>
      )}
      <LoanCommentsSideCardPreview
        show={show}
        onClose={setShow}
        loan={params?.data}
        comments={loanComments}
      />
    </>
  );
};

const LoanCommentsBadge = ({ loanComments, setShow }) => {
  const styles = useMemo(() => {
    return {
      iconButton: {
        position: "relative",
      },
      badge: {
        ".MuiBadge-badge": {
          fontSize: "8px",
          height: "14px",
          minWidth: "14px",
          p: 0,
        },
      },
      iconButtonIcon: { fontSize: "14px" },
    };
  }, []);
  if (isEmpty(loanComments)) {
    return (
      <SpeakerNotesOffOutlined
        color={"inherit"}
        className="thin"
        sx={{ ...styles.iconButtonIcon }}
      />
    );
  }
  return (
    <Tooltip title="View Comments">
      <Box sx={styles.iconButton}>
        <Badge
          sx={styles.badge}
          badgeContent={size(loanComments)}
          color={isEmpty(loanComments) ? "info" : "primary"}
        >
          <ChatBubbleOutline
            color={isEmpty(loanComments) ? "info" : "primary"}
            className="thin"
            sx={styles.iconButtonIcon}
          />
        </Badge>
      </Box>
    </Tooltip>
  );
};

export default CellRendererLoanComments;
